import { useCallback, useEffect, useRef, useState } from 'react'
import type { KeyboardEvent, ReactNode } from 'react'
import type { Leave } from '@lms/shared-types'
import { LEAVE_TYPE_ACCENT, LEAVE_TYPE_LABEL, ownerFacingStatus } from '../../lib/leave-type-meta'
import { fmtDateRangeLong } from '../../lib/format'
import { canRequestCancellation } from '../../lib/leave-cancellation'
import { Badge } from '../primitives/Badge'
import { Icon, type IconName } from '../primitives/Icon'

// Status label map — matches prototype StatusBadge labels
export const STATUS_LABEL: Record<Leave['status'], string> = {
  draft: 'Draft',
  pending_hr_review: 'Pending HR review',
  pending: 'Pending',
  escalated: 'Escalated',
  approved: 'Approved',
  rejected: 'Rejected',
  cancelled: 'Cancelled',
  pending_cancellation: 'Cancellation requested',
}

// Prototype StatusBadge class map — badge.dot + colour variant
export const STATUS_BADGE_CLASS: Record<Leave['status'], string> = {
  draft: 'badge dot b-gray',
  pending_hr_review: 'badge dot b-warning',
  pending: 'badge dot b-warning',
  escalated: 'badge dot b-warning',
  approved: 'badge dot b-success',
  rejected: 'badge dot b-danger',
  cancelled: 'badge dot b-gray',
  pending_cancellation: 'badge dot b-warning',
}

interface LeaveHistoryTableProps {
  leaves: ReadonlyArray<Leave>
  onOpen: (leaveId: string) => void
  onCancel?: (leave: Leave) => void
  /** When true, rows in pending/escalated state show an inline Cancel button. */
  cancellable?: boolean
  /** When true, an approved row shows a "Request cancellation" button that
   *  routes to the manager (gated by the cancelApprovedRequiresApproval flag). */
  allowRequestCancellation?: boolean
  /** Rendered inside the card beneath the table (e.g. PaginationControls). */
  footer?: ReactNode
  /** When true, this is the requester's OWN list, so the HR-review holding
   *  state is shown as plain "Pending" (see ownerFacingStatus). Manager / HR
   *  tables leave this false and see the true status. Display-only. */
  ownerView?: boolean
}

/**
 * Lumen-prototype leave history table — TYPE / DATES / DAYS / REASON /
 * STATUS / (actions). Uses the .card + .tbl vocabulary from
 * prototype/Lumen.html so the rendered output matches the design 1-to-1.
 */
export function LeaveHistoryTable({
  leaves,
  onOpen,
  onCancel,
  cancellable = false,
  allowRequestCancellation = false,
  footer,
  ownerView = false,
}: LeaveHistoryTableProps): JSX.Element {
  return (
    // Horizontal scroll on narrow viewports instead of clipping the right
    // columns (Reason / Status / Actions) — the `.card` used to carry
    // `overflow: hidden` to clip its rounded corners, which also blocked any
    // scroll, so on mobile the Submitted column truncated and everything to
    // its right vanished. The min-width keeps the columns from being crushed
    // below their natural width so the table reads correctly before the user
    // needs to scroll. Matches the `overflowX: 'auto'` wrapper used by every
    // other wide table in the app (TableCard, MatrixCard, import previews…).
    <div className="card" style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
      <table className="tbl" style={{ minWidth: 640 }}>
        <thead>
          <tr>
            <th>Type</th>
            <th>Dates</th>
            <th>Days</th>
            <th>Reason</th>
            <th>Status</th>
            <th aria-label="Actions" />
          </tr>
        </thead>
        <tbody>
          {leaves.map((leave) => (
            <LeaveRow
              key={leave.id}
              leave={leave}
              onOpen={onOpen}
              onCancel={onCancel}
              cancellable={cancellable}
              allowRequestCancellation={allowRequestCancellation}
              ownerView={ownerView}
            />
          ))}
        </tbody>
      </table>
      {footer}
    </div>
  )
}

interface LeaveRowProps {
  leave: Leave
  onOpen: (leaveId: string) => void
  onCancel: ((leave: Leave) => void) | undefined
  cancellable: boolean
  allowRequestCancellation: boolean
  ownerView: boolean
}

function LeaveRow({
  leave,
  onOpen,
  onCancel,
  cancellable,
  allowRequestCancellation,
  ownerView,
}: LeaveRowProps): JSX.Element {
  // Display-only: the requester's own list masks pending_hr_review as pending.
  // Action-gating below still keys off the real leave.status.
  const shownStatus = ownerView ? ownerFacingStatus(leave.status) : leave.status
  const days = computeDays(leave)
  const dateRange = fmtDateRangeLong(leave.startDate, leave.endDate)
  const isRequestCancel =
    allowRequestCancellation && canRequestCancellation(leave)
  const canCancel =
    cancellable &&
    typeof onCancel === 'function' &&
    (leave.status === 'pending' ||
      leave.status === 'escalated' ||
      // Owner may withdraw a leave still awaiting HR review (shown as "Pending").
      leave.status === 'pending_hr_review' ||
      isRequestCancel)

  const handleOpen = (): void => onOpen(leave.id)
  const handleKeyDown = (event: React.KeyboardEvent<HTMLTableRowElement>): void => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      handleOpen()
    }
  }

  return (
    <tr
      data-testid="leave-row"
      tabIndex={0}
      role="button"
      aria-label={`View ${LEAVE_TYPE_LABEL[leave.type]} leave ${dateRange}`}
      onClick={handleOpen}
      onKeyDown={handleKeyDown}
      style={{ cursor: 'pointer' }}
    >
      <td>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span
            aria-hidden
            style={{
              width: 8,
              height: 8,
              borderRadius: 4,
              background: LEAVE_TYPE_ACCENT[leave.type],
              flexShrink: 0,
            }}
          />
          {LEAVE_TYPE_LABEL[leave.type]}
          {leave.isEmergency ? (
            <Badge
              variant="danger"
              tooltip="Annual leave filed with less than 7 days notice. Classified as Emergency Leave (Policy §1.1)."
            >
              Emergency
            </Badge>
          ) : null}
          {leave.isOutOfSystem ? (
            <Badge
              variant="info"
              tooltip="Encoded by HR out of system (approved offline, e.g. unpaid or blackout-period leave)."
            >
              Out of system
            </Badge>
          ) : null}
          {leave.approvedOverCapacity ? (
            <Badge
              variant="warning"
              tooltip="Approved while the team was over its concurrent-absence cap."
            >
              Above capacity
            </Badge>
          ) : null}
        </span>
      </td>
      <td style={{ fontSize: 12.5, whiteSpace: 'nowrap' }}>
        {dateRange}
      </td>
      <td style={{ fontVariantNumeric: 'tabular-nums' }}>{days}</td>
      <td style={{ color: 'var(--text-2)', fontSize: 12.5, maxWidth: 280 }}>
        <span
          style={{
            display: 'inline-block',
            maxWidth: 260,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            verticalAlign: 'middle',
          }}
        >
          {leave.reason ?? '—'}
        </span>
      </td>
      <td>
        <span className={STATUS_BADGE_CLASS[shownStatus]}>
          {STATUS_LABEL[shownStatus]}
        </span>
      </td>
      <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
        <RowActionsMenu
          leave={leave}
          onOpen={onOpen}
          onCancel={onCancel}
          canCancel={canCancel}
          isRequestCancel={isRequestCancel}
        />
      </td>
    </tr>
  )
}

export function computeDays(leave: Leave): string {
  // Prefer the server-computed count — it subtracts public holidays (and
  // half-days) exactly as the detail page and balance do, which the
  // client-side weekday heuristic below cannot (it has no holiday list, so it
  // over-counts a leave spanning a public holiday). Fall back to the heuristic
  // only when the server didn't supply `days`.
  if (leave.days != null) return formatDayCount(leave.days)
  // Working-day count: weekdays between start and end inclusive, minus
  // 0.5 per side-specific half-day flag. Public holidays aren't excluded
  // here — the server-side balance accounting handles those; this is a
  // read-only display heuristic that matches what employees expect to
  // see in a history table.
  const start = new Date(`${leave.startDate}T00:00:00Z`)
  const end = new Date(`${leave.endDate}T00:00:00Z`)
  let weekdays = 0
  for (let t = start.getTime(); t <= end.getTime(); t += 86_400_000) {
    const day = new Date(t).getUTCDay()
    if (day !== 0 && day !== 6) weekdays += 1
  }
  // Single-day half-day collapses to 0.5 regardless of which side flags are
  // set. HR-created half-days can carry BOTH halfStart and halfEnd, which
  // would otherwise subtract a full day (1 - 0.5 - 0.5 = 0). Mirror the
  // server (`leave_rules.compute_leave_days` / `leave_decisions._recompute`).
  const anyHalf = leave.halfStart || leave.halfEnd || leave.halfDay
  if (leave.startDate === leave.endDate) {
    return formatDayCount(anyHalf && weekdays >= 1 ? 0.5 : weekdays)
  }
  let total = weekdays
  if (leave.halfStart) total -= 0.5
  if (leave.halfEnd) total -= 0.5
  if (!leave.halfStart && !leave.halfEnd && leave.halfDay) total -= 0.5
  if (total < 0) total = 0
  return formatDayCount(total)
}

function formatDayCount(n: number): string {
  return Number.isInteger(n) ? String(n) : n.toFixed(1).replace(/\.0$/, '')
}

interface RowActionsMenuProps {
  leave: Leave
  onOpen: (leaveId: string) => void
  onCancel: ((leave: Leave) => void) | undefined
  canCancel: boolean
  isRequestCancel: boolean
}

/**
 * Per-row overflow (kebab) menu. Collapses the previously-inline row actions
 * (Details / Cancel) behind a single `⋮` trigger so the table reads cleaner.
 *
 * Accessibility mirrors ProfileMenu: aria-haspopup + role="menu", Escape
 * closes and restores focus, click-outside closes, ↑↓ move between items,
 * and the panel flips above the trigger when it would overflow the viewport
 * bottom (rows near the page fold).
 */
function RowActionsMenu({
  leave,
  onOpen,
  onCancel,
  canCancel,
  isRequestCancel,
}: Readonly<RowActionsMenuProps>): JSX.Element {
  const [open, setOpen] = useState(false)
  const [flipUp, setFlipUp] = useState(false)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)

  // ── close on outside click ──────────────────────────────────────────────────
  useEffect(() => {
    if (!open) return
    function handleClick(e: MouseEvent): void {
      if (
        panelRef.current &&
        triggerRef.current &&
        !panelRef.current.contains(e.target as Node) &&
        !triggerRef.current.contains(e.target as Node)
      ) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [open])

  // ── flip above the trigger when the panel would clip the viewport bottom ────
  useEffect(() => {
    if (!open) return
    const rect = triggerRef.current?.getBoundingClientRect()
    if (!rect) return
    const estimatedPanelHeight = canCancel ? 96 : 52
    setFlipUp(rect.bottom + estimatedPanelHeight > window.innerHeight)
  }, [open, canCancel])

  // ── focus first item when the panel opens ───────────────────────────────────
  useEffect(() => {
    if (!open) return
    const id = setTimeout(() => {
      panelRef.current?.querySelector<HTMLElement>('[role="menuitem"]')?.focus()
    }, 0)
    return () => clearTimeout(id)
  }, [open])

  const close = useCallback((): void => {
    setOpen(false)
    triggerRef.current?.focus()
  }, [])

  const handlePanelKeyDown = useCallback(
    (e: KeyboardEvent<HTMLDivElement>): void => {
      if (e.key === 'Escape') {
        close()
        return
      }
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault()
        const items = Array.from(
          panelRef.current?.querySelectorAll<HTMLElement>('[role="menuitem"]') ?? [],
        )
        if (items.length === 0) return
        const idx = items.findIndex((el) => el === document.activeElement)
        const next =
          e.key === 'ArrowDown'
            ? (idx + 1) % items.length
            : (idx - 1 + items.length) % items.length
        items[next]?.focus()
      }
    },
    [close],
  )

  return (
    <div style={{ position: 'relative', display: 'inline-flex' }}>
      <button
        ref={triggerRef}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Row actions"
        data-testid="leave-row-actions"
        onClick={(event) => {
          event.stopPropagation()
          setOpen((prev) => !prev)
        }}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 32,
          height: 32,
          padding: 0,
          background: open ? 'var(--surface-2)' : 'transparent',
          border: '1px solid transparent',
          borderRadius: 8,
          cursor: 'pointer',
          color: 'var(--text-2)',
          transition: 'background var(--motion-duration-fast) var(--motion-ease-out)',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = 'var(--surface-2)'
        }}
        onMouseLeave={(e) => {
          if (!open) e.currentTarget.style.background = 'transparent'
        }}
      >
        <Icon name="more_vert" size={18} aria-hidden />
      </button>

      {open && (
        <div
          ref={panelRef}
          role="menu"
          data-testid="leave-row-actions-menu"
          onKeyDown={handlePanelKeyDown}
          onClick={(event) => event.stopPropagation()}
          style={{
            position: 'absolute',
            right: 0,
            [flipUp ? 'bottom' : 'top']: 'calc(100% + 4px)',
            minWidth: 176,
            background: 'var(--surface)',
            border: '1px solid var(--border)',
            borderRadius: 10,
            boxShadow: 'var(--shadow-2)',
            zIndex: 100,
            overflow: 'hidden',
            padding: '6px 0',
            animation: 'lms-row-actions-in var(--motion-duration-fast) var(--motion-ease-out)',
          }}
        >
          <style>{`
            @keyframes lms-row-actions-in {
              from { opacity: 0; transform: translateY(-4px); }
              to   { opacity: 1; transform: translateY(0); }
            }
            @media (prefers-reduced-motion: reduce) {
              @keyframes lms-row-actions-in {
                from { opacity: 0; }
                to   { opacity: 1; }
              }
            }
          `}</style>

          <RowActionItem
            icon="visibility"
            label="Details"
            testId="leave-details-button"
            onClick={() => {
              close()
              onOpen(leave.id)
            }}
          />
          {canCancel ? (
            <RowActionItem
              icon="close"
              label={isRequestCancel ? 'Request cancellation' : 'Cancel'}
              testId="cancel-leave"
              destructive
              onClick={() => {
                close()
                onCancel?.(leave)
              }}
            />
          ) : null}
        </div>
      )}
    </div>
  )
}

interface RowActionItemProps {
  icon: IconName
  label: string
  onClick: () => void
  testId: string
  destructive?: boolean
}

function RowActionItem({
  icon,
  label,
  onClick,
  testId,
  destructive = false,
}: Readonly<RowActionItemProps>): JSX.Element {
  const [hover, setHover] = useState(false)
  return (
    <button
      type="button"
      role="menuitem"
      data-testid={testId}
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '8px 14px',
        background: hover
          ? destructive
            ? 'color-mix(in srgb, var(--danger) 8%, transparent)'
            : 'var(--surface-2)'
          : 'transparent',
        border: 'none',
        cursor: 'pointer',
        color: destructive ? 'var(--danger)' : 'var(--text)',
        fontFamily: 'var(--font-sans)',
        fontSize: 13,
        textAlign: 'left',
        transition: 'background var(--motion-duration-fast) var(--motion-ease-out)',
        outline: 'none',
      }}
    >
      <Icon name={icon} size={16} aria-hidden />
      {label}
    </button>
  )
}
